import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, StatusPill } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { PhoneInput } from "@/components/PhoneInput";
import { useAuth } from "@/context/AuthContext";
import { dmy, titleize } from "@/lib/format";

const EMPTY = { name: "", email: "", phone: "", role: "", department: "", employee_id: "",
  joining_date: "", salary: "0", salary_frequency: "monthly", salary_effective_from: "",
  compensation_type: "salary", percentage: "0", percentage_base: "",
  permissions: {} };

export default function Staff() {
  const { can, user } = useAuth();
  const [items, setItems] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [formError, setFormError] = useState("");
  const [permFor, setPermFor] = useState(null);
  const [perms, setPerms] = useState({});
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", phone: "", department: "", employee_id: "",
    salary: "0", salary_frequency: "monthly", salary_effective_from: "",
    compensation_type: "salary", percentage: "0", percentage_base: "" });

  const load = useCallback(async () => {
    setItems(null);
    try {
      const [s, c] = await Promise.all([api.get("/staff"), api.get("/permissions/catalog")]);
      setItems(s.data.items); setCatalog(c.data);
    } catch (e) { setError(apiError(e)); }
  }, []);

  useEffect(() => { if (can("staff.manage")) load(); }, [load, can]);
  if (!can("staff.manage")) return <NoAccess what="staff management" />;

  const allowedRoles = [...(catalog?.roles || []).filter((r) => {
    const mods = user?.modules || [];
    if (r.startsWith("hospital") || ["doctor", "nurse", "receptionist", "billing_executive",
      "lab_technician", "ward_staff"].includes(r)) return mods.includes("hospital");
    if (r.startsWith("pharmacy")) return mods.includes("pharmacy");
    if (r === "business_admin") return mods.length === 2;
    return true;
  }), ...((catalog?.custom_roles || []).map((c) => c.role_key))];

  const submit = async (e) => {
    e.preventDefault(); setFormError("");
    try {
      const { data } = await api.post("/staff", { ...form, salary: Number(form.salary || 0),
        percentage: Number(form.percentage || 0) });
      toast.success(data.message);
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { setFormError(apiError(err)); }
  };

  const openEdit = (s) => {
    setEditing(s);
    setEditForm({ name: s.name || "", phone: s.phone || "", department: s.department || "",
      employee_id: s.employee_id || "", salary: String(s.salary ?? 0),
      salary_frequency: s.salary_frequency || "monthly",
      salary_effective_from: (s.salary_effective_from || "").slice(0, 10),
      compensation_type: s.compensation_type || "salary", percentage: String(s.percentage ?? 0),
      percentage_base: s.percentage_base || "" });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    const body = { name: editForm.name, phone: editForm.phone, department: editForm.department,
      employee_id: editForm.employee_id };
    if (Number(editForm.salary || 0) !== Number(editing.salary ?? 0)) {
      body.salary = Number(editForm.salary || 0);
      body.salary_frequency = editForm.salary_frequency;
      body.salary_effective_from = editForm.salary_effective_from;
    }
    if (editForm.compensation_type !== (editing.compensation_type || "salary")
        || Number(editForm.percentage || 0) !== Number(editing.percentage || 0)
        || (editForm.percentage_base || "") !== (editing.percentage_base || "")) {
      body.compensation_type = editForm.compensation_type;
      body.percentage = Number(editForm.percentage || 0);
      body.percentage_base = editForm.percentage_base;
    }
    try {
      await api.patch(`/staff/${editing.user_id}`, body);
      toast.success("Staff member updated. Past payroll records are unchanged.");
      setEditing(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const openPerms = (s) => {
    setPermFor(s);
    const current = {};
    (catalog.groups || []).forEach((g) => g.permissions.forEach((p) => {
      current[p] = Boolean(s.effective_permissions?.[p]);
    }));
    setPerms(current);
  };

  const savePerms = async () => {
    try {
      await api.put(`/staff/${permFor.user_id}/permissions`, { permissions: perms });
      toast.success("Permissions updated and enforced server-side.");
      setPermFor(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggleStatus = async (s) => {
    const status = s.status === "active" ? "suspended" : "active";
    try {
      await api.patch(`/staff/${s.user_id}`, { status });
      toast.success(`Staff ${status}.`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (error) return <ErrorState message={error} />;
  if (!items || !catalog) return <Loading />;

  return (
    <div data-testid="staff-page">
      <PageHeader overline="Administration" title="Staff & permissions"
        subtitle="Create staff, assign a role, then fine-tune individual permissions with toggles. Every toggle is enforced on the server."
        actions={<button onClick={() => setOpen(true)} className={primaryBtnCls} data-testid="add-staff-btn">Create staff</button>} />

      <Table testid="staff-table" columns={["Name", "Email", "Role", "Department", "Salary", "Status", "Actions"]}
        rows={items} empty={<EmptyState title="No staff yet" subtitle="Create your first staff member; they receive a secure setup email." testid="staff-empty" />}
        renderRow={(s) => (
          <tr key={s.user_id} data-testid={`staff-row-${s.user_id}`}>
            <Td className="font-medium text-slate-900">{s.name}</Td>
            <Td className="text-slate-600">{s.email}</Td>
            <Td className="text-slate-600">{titleize(s.role)}</Td>
            <Td className="text-slate-600">{s.department || "—"}</Td>
            <Td className="text-slate-600" data-testid={`staff-salary-${s.user_id}`}>
              {s.compensation_type === "percentage"
                ? `${s.percentage || 0}% · ${s.percentage_base || "sales"}`
                : s.compensation_type === "contract"
                  ? (s.salary ? `Contract ₹${s.salary} / ${s.salary_frequency || "monthly"}` : "Contract")
                  : (s.salary ? `₹${s.salary} / ${s.salary_frequency || "monthly"}` : "—")}
            </Td>
            <Td><StatusPill status={s.status} /></Td>
            <Td>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => openEdit(s)} className={ghostBtnCls} data-testid={`edit-staff-${s.user_id}`}>Edit</button>
                {can("permissions.manage") && s.user_id !== user.user_id && (
                  <button onClick={() => openPerms(s)} className={ghostBtnCls} data-testid={`permissions-${s.user_id}`}>Permissions</button>
                )}
                {s.user_id !== user.user_id && (
                  <button onClick={() => toggleStatus(s)} className={ghostBtnCls} data-testid={`toggle-staff-${s.user_id}`}>
                    {s.status === "active" ? "Suspend" : "Activate"}
                  </button>
                )}
              </div>
            </Td>
          </tr>
        )} />

      <Modal open={Boolean(editing)} onClose={() => setEditing(null)} wide title="Edit staff member"
        subtitle={editing?.name} testid="edit-staff-modal">
        <form onSubmit={saveEdit} className="grid gap-5 sm:grid-cols-2" data-testid="edit-staff-form">
          <Field label="Full name">
            <input className={inputCls} value={editForm.name} data-testid="edit-staff-name-input"
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
          </Field>
          <Field label="Phone">
            <input className={inputCls} value={editForm.phone}
              onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
          </Field>
          <Field label="Department">
            <input className={inputCls} value={editForm.department}
              onChange={(e) => setEditForm({ ...editForm, department: e.target.value })} />
          </Field>
          <Field label="Employee ID">
            <input className={inputCls} value={editForm.employee_id}
              onChange={(e) => setEditForm({ ...editForm, employee_id: e.target.value })} />
          </Field>
          <Field label="Salary / Contract amount (₹)">
            <input type="number" min="0" className={inputCls} value={editForm.salary}
              data-testid="edit-staff-salary-input"
              onChange={(e) => setEditForm({ ...editForm, salary: e.target.value })} />
          </Field>
          <Field label="Salary frequency">
            <select className={inputCls} value={editForm.salary_frequency}
              onChange={(e) => setEditForm({ ...editForm, salary_frequency: e.target.value })}>
              {["monthly", "weekly", "daily", "hourly"].map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
          <Field label="Salary effective from">
            <input type="date" className={inputCls} value={editForm.salary_effective_from}
              data-testid="edit-staff-effective-input"
              onChange={(e) => setEditForm({ ...editForm, salary_effective_from: e.target.value })} />
          </Field>
          <Field label="Compensation type">
            <select className={inputCls} data-testid="edit-staff-comp-type-select" value={editForm.compensation_type}
              onChange={(e) => setEditForm({ ...editForm, compensation_type: e.target.value })}>
              <option value="salary">Salary</option>
              <option value="contract">Contract</option>
              <option value="percentage">Percentage</option>
            </select>
          </Field>
          {editForm.compensation_type === "percentage" && (
            <>
              <Field label="Percentage (%)">
                <input type="number" min="0" max="100" step="0.01" className={inputCls} data-testid="edit-staff-percentage-input"
                  value={editForm.percentage} onChange={(e) => setEditForm({ ...editForm, percentage: e.target.value })} />
              </Field>
              <Field label="Percentage base">
                <input className={inputCls} data-testid="edit-staff-percentage-base-input" value={editForm.percentage_base}
                  onChange={(e) => setEditForm({ ...editForm, percentage_base: e.target.value })} />
              </Field>
            </>
          )}
          <div className="sm:col-span-2">
            <p className="mb-4 text-xs text-slate-500">
              Changing the salary adds a new salary record from the effective date. Payroll already
              run for earlier months is never rewritten.
            </p>
            <button className={primaryBtnCls} data-testid="edit-staff-submit-btn">Save changes</button>
          </div>
        </form>
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} wide title="Create staff"
        subtitle="An invitation email is sent so the staff member sets their own password." testid="add-staff-modal">
        <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2">
          <Field label="Full name *"><input required data-testid="staff-name-input" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Email *"><input required type="email" data-testid="staff-email-input" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <PhoneInput label="Mobile" required value={form.phone} testid="staff-phone"
            onChange={(v) => setForm({ ...form, phone: v })} />
          <Field label="Role *">
            <select required className={inputCls} value={form.role} data-testid="staff-role-select"
              onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="">Select role</option>
              {allowedRoles.map((r) => <option key={r} value={r}>{(catalog.custom_roles || []).find((c) => c.role_key === r)?.name || titleize(r)}</option>)}
            </select>
          </Field>
          <Field label="Department"><input className={inputCls} value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></Field>
          <Field label="Employee ID"><input className={inputCls} data-testid="staff-employee-id-input" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} /></Field>
          <Field label="Joining date"><input type="date" className={inputCls} data-testid="staff-joining-date-input" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} /></Field>
          <Field label="Salary / Contract amount (₹)"><input type="number" min="0" className={inputCls} data-testid="staff-salary-input" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} /></Field>
          <Field label="Salary frequency">
            <select className={inputCls} data-testid="staff-salary-frequency-select" value={form.salary_frequency}
              onChange={(e) => setForm({ ...form, salary_frequency: e.target.value })}>
              {["monthly", "weekly", "daily", "hourly"].map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </Field>
          <Field label="Salary effective from"><input type="date" className={inputCls} data-testid="staff-salary-effective-input" value={form.salary_effective_from} onChange={(e) => setForm({ ...form, salary_effective_from: e.target.value })} /></Field>
          <Field label="Compensation type">
            <select className={inputCls} data-testid="staff-comp-type-select" value={form.compensation_type}
              onChange={(e) => setForm({ ...form, compensation_type: e.target.value })}>
              <option value="salary">Salary</option>
              <option value="contract">Contract</option>
              <option value="percentage">Percentage</option>
            </select>
          </Field>
          {form.compensation_type === "percentage" && (
            <>
              <Field label="Percentage (%) *">
                <input type="number" min="0" max="100" step="0.01" className={inputCls} data-testid="staff-percentage-input"
                  value={form.percentage} onChange={(e) => setForm({ ...form, percentage: e.target.value })} />
              </Field>
              <Field label="Percentage base">
                <input className={inputCls} data-testid="staff-percentage-base-input" value={form.percentage_base}
                  placeholder="e.g. pharmacy_sales, collections" onChange={(e) => setForm({ ...form, percentage_base: e.target.value })} />
              </Field>
            </>
          )}
          <Field label="CV / document (PDF or image)" className="sm:col-span-2">
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" data-testid="staff-document-input"
              className="text-sm text-slate-700"
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;
                if (file.size > 3 * 1024 * 1024) { toast.error("Document must be under 3 MB."); return; }
                const r = new FileReader();
                r.onload = () => setForm((f) => ({ ...f, document_name: file.name, document_data: r.result }));
                r.readAsDataURL(file);
              }} />
            {form.document_name && <p className="mt-2 text-xs text-slate-600">{form.document_name}</p>}
          </Field>
          {form.role && (
            <div className="sm:col-span-2 space-y-6">
              <p className="overline text-[#EA580C]">Permissions (defaults apply if left off)</p>
              {catalog.groups.map((g) => (
                <div key={g.key}>
                  <p className="text-sm font-semibold text-slate-900 mb-3">{g.label}</p>
                  <div className="grid gap-2 sm:grid-cols-2">
                    {g.permissions.map((p) => {
                      const on = form.permissions[p] ?? (catalog.role_defaults[form.role] || []).includes(p);
                      return (
                        <label key={p} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3.5 py-2 text-sm">
                          <span className="text-slate-700">{catalog.labels?.[p] || p}</span>
                          <input type="checkbox" checked={on} data-testid={`new-perm-${p}`}
                            onChange={(e) => setForm({ ...form, permissions: { ...form.permissions, [p]: e.target.checked } })}
                            className="h-4 w-4 accent-[#0F172A]" />
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
          {formError && <p className="sm:col-span-2 text-sm text-red-600" data-testid="staff-form-error">{formError}</p>}
          <div className="sm:col-span-2"><button className={primaryBtnCls} data-testid="staff-submit-btn">Create staff & send invite</button></div>
        </form>
      </Modal>

      <Modal open={Boolean(permFor)} onClose={() => setPermFor(null)} wide
        title={`Permissions — ${permFor?.name || ""}`} subtitle="Explicit toggles override the role defaults." testid="permissions-modal">
        {permFor && (
          <div className="space-y-8">
            {catalog.groups.map((g) => (
              <div key={g.key}>
                <p className="text-sm font-semibold text-slate-900 mb-3">{g.label}</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {g.permissions.map((p) => (
                    <label key={p} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3.5 py-2 text-sm">
                      <span className="text-slate-700">{catalog.labels?.[p] || p}</span>
                      <input type="checkbox" checked={Boolean(perms[p])} data-testid={`perm-toggle-${p}`}
                        onChange={(e) => setPerms({ ...perms, [p]: e.target.checked })}
                        className="h-4 w-4 accent-[#0F172A]" />
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <button onClick={savePerms} className={primaryBtnCls} data-testid="save-permissions-btn">Save permissions</button>
          </div>
        )}
      </Modal>
    </div>
  );
}
